/* eslint-disable @typescript-eslint/no-unsafe-argument -- Three.js declares Mesh as `Mesh<any, any, any>` */
import * as THREE from "three";
import { isNil, isNotNil } from "es-toolkit";
import {
  MuscleClassifier,
  type MuscleClassification,
} from "@/entities/muscle/ui/muscle-classification.utils";

type LimbSide = "l" | "r";
type LimbSegment = "upperarm" | "forearm" | "hand" | "thigh" | "shin" | "foot";

export type MeshUserData = {
  classification?: MuscleClassification;
  clonedMaterial?: THREE.Material;
};

export const SKIN_COLOR = "#caa68f";
export const SKIN_DIM_COLOR = "#9b806c";

export type Highlight = "primary" | "secondary" | "missed" | "load-under" | "load-on" | "load-over";

export const HIGHLIGHT_COLOR = {
  primary: "#ef4444",
  secondary: "#ef4444",
  missed: "#3b82f6",
  "load-under": "#3b82f6",
  "load-on": "#10b981",
  "load-over": "#f59e0b",
} as const satisfies Record<Highlight, string>;

export const EMISSIVE_STRENGTH = {
  primary: 0.55,
  secondary: 0.25,
  missed: 0.4,
  "load-under": 0.3,
  "load-on": 0.5,
  "load-over": 0.45,
} as const satisfies Record<Highlight, number>;

const FOOT_MUSCLE_RE = / of foot|hallucis|metatars|plantar|calcane/i;
const HAND_MUSCLE_RE = / of hand|metacarp|hypothenar|thenar|\bpalmar\b/i;
const FOREARM_RE = /brachioradialis|flexor carpi|extensor carpi|pronator|supinator|anconeus/i;
const UPPERARM_RE = /brachii|brachialis|coracobrachialis|deltoid/i;
const HAND_FINGER_RE = /finger of hand|of hand/i;
const FOOT_FINGER_RE = /finger of foot/i;
const SHIN_RE = /gastrocnemius|\bsoleus\b|tibialis|peroneus|fibularis|popliteus|triceps surae/i;
const THIGH_RE =
  /femoris|vastus|sartorius|gracilis|adductor(?! hallucis)|semimembranosus|semitendinosus|pectineus|tensor fasciae/i;

type LimbGroups = Record<LimbSide, Record<LimbSegment, Array<THREE.Mesh>>>;
export type LimbPivots = Partial<Record<string, THREE.Object3D>>;

type ChainConfig = {
  upper: LimbSegment;
  lower: LimbSegment;
  tip: LimbSegment;
  topKey: string;
  midKey: string;
};

const ARM_CONFIG: ChainConfig = {
  upper: "upperarm",
  lower: "forearm",
  tip: "hand",
  topKey: "Shoulder",
  midKey: "Elbow",
};

const LEG_CONFIG: ChainConfig = {
  upper: "thigh",
  lower: "shin",
  tip: "foot",
  topKey: "Hip",
  midKey: "Knee",
};

export class LimbRigging {
  private static detectLimbSegment(joinedName: string): LimbSegment | null {
    const isFootMuscle = FOOT_MUSCLE_RE.test(joinedName);
    const isHandMuscle = HAND_MUSCLE_RE.test(joinedName);
    if (!isHandMuscle && FOREARM_RE.test(joinedName)) return "forearm";

    if (UPPERARM_RE.test(joinedName)) return "upperarm";

    if (isHandMuscle || HAND_FINGER_RE.test(joinedName)) return "hand";

    if (isFootMuscle || FOOT_FINGER_RE.test(joinedName)) return "foot";

    if (SHIN_RE.test(joinedName)) return "shin";

    if (THIGH_RE.test(joinedName)) return "thigh";

    return null;
  }

  private static classifyLimb(joinedName: string): { side: LimbSide; segment: LimbSegment } | null {
    const lastNamePart = joinedName.split("|").pop()?.trim() ?? "";
    const sideMatch = /\.[a-z0-9]*([lr])$/i.exec(lastNamePart);
    if (isNil(sideMatch)) return null;

    const sideChar = sideMatch[1]?.toLowerCase();
    if (sideChar !== "l" && sideChar !== "r") return null;

    const segment = LimbRigging.detectLimbSegment(joinedName);

    return isNil(segment) ? null : { side: sideChar, segment };
  }

  private static buildEmptySegmentMap(): Record<LimbSegment, Array<THREE.Mesh>> {
    return {
      upperarm: [],
      forearm: [],
      hand: [],
      thigh: [],
      shin: [],
      foot: [],
    };
  }

  private static buildEmptyGroups(): LimbGroups {
    return { l: LimbRigging.buildEmptySegmentMap(), r: LimbRigging.buildEmptySegmentMap() };
  }

  private static boxCenterX(box: THREE.Box3): number {
    return (box.min.x + box.max.x) / 2;
  }

  private static boxCenterZ(box: THREE.Box3): number {
    return (box.min.z + box.max.z) / 2;
  }

  private static computeWorldBox(meshes: ReadonlyArray<THREE.Mesh>): THREE.Box3 {
    const box = new THREE.Box3();
    for (const mesh of meshes) box.union(new THREE.Box3().setFromObject(mesh));

    return box;
  }

  private static collectGroups(root: THREE.Object3D): LimbGroups {
    const groups = LimbRigging.buildEmptyGroups();

    root.traverse((object) => {
      if (!(object instanceof THREE.Mesh) || !object.visible) return;

      const chain = MuscleClassifier.buildNameChain(object).join(" | ");
      const limb = LimbRigging.classifyLimb(chain);
      if (isNotNil(limb)) groups[limb.side][limb.segment].push(object);
    });

    return groups;
  }

  private static jointBoxTop(box: THREE.Box3): THREE.Vector3 {
    return new THREE.Vector3(LimbRigging.boxCenterX(box), box.max.y, LimbRigging.boxCenterZ(box));
  }

  private static createPivot(
    name: string,
    parent: THREE.Object3D,
    world: THREE.Vector3,
  ): THREE.Object3D {
    const pivot = new THREE.Object3D();
    pivot.name = name;
    parent.add(pivot);
    parent.updateMatrixWorld(true);
    pivot.position.copy(parent.worldToLocal(world.clone()));
    pivot.updateMatrixWorld(true);

    return pivot;
  }

  private static buildChain(
    args: Readonly<{
      root: THREE.Object3D;
      groups: LimbGroups;
      pivots: LimbPivots;
      side: LimbSide;
      config: ChainConfig;
    }>,
  ): void {
    const { root, groups, pivots, side, config } = args;
    const upperMeshes = groups[side][config.upper];
    const lowerMeshes = groups[side][config.lower];
    const tipMeshes = groups[side][config.tip];
    if (upperMeshes.length === 0 || lowerMeshes.length === 0) return;

    const upperBox = LimbRigging.computeWorldBox(upperMeshes);
    const lowerBox = LimbRigging.computeWorldBox(lowerMeshes);
    if (upperBox.isEmpty() || lowerBox.isEmpty()) return;

    const outerPivot = LimbRigging.createPivot(
      `pivot_${side}${config.topKey}`,
      root,
      LimbRigging.jointBoxTop(upperBox),
    );
    const innerPivot = LimbRigging.createPivot(
      `pivot_${side}${config.midKey}`,
      outerPivot,
      LimbRigging.jointBoxTop(lowerBox),
    );

    for (const mesh of upperMeshes) outerPivot.attach(mesh);
    for (const mesh of [...lowerMeshes, ...tipMeshes]) innerPivot.attach(mesh);

    pivots[side + config.topKey] = outerPivot;
    pivots[side + config.midKey] = innerPivot;
  }

  static rig(root: THREE.Object3D): LimbPivots {
    let topAncestor: THREE.Object3D = root;
    while (isNotNil(topAncestor.parent)) topAncestor = topAncestor.parent;
    topAncestor.updateMatrixWorld(true);

    const groups = LimbRigging.collectGroups(root);
    const pivots: LimbPivots = {};

    LimbRigging.buildChain({ root, groups, pivots, side: "l", config: ARM_CONFIG });
    LimbRigging.buildChain({ root, groups, pivots, side: "r", config: ARM_CONFIG });
    LimbRigging.buildChain({ root, groups, pivots, side: "l", config: LEG_CONFIG });
    LimbRigging.buildChain({ root, groups, pivots, side: "r", config: LEG_CONFIG });

    return pivots;
  }
}
